import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Sparkles,
    Wand2,
    Newspaper,
    Pin,
    PinOff,
    Image as ImageIcon,
    Link2,
    Send,
    Loader2,
    Trash2,
    RefreshCw,
    Eye,
    Bell,
    Edit3,
    ChevronDown,
    Check,
    AlertCircle,
} from "lucide-react";
import api from "../../lib/api";
import ThemedSelect from "../../components/ThemedSelect";
import FileUpload from "../../components/FileUpload";

const CATEGORIES = [
    { value: "info", label: "Info", color: "bg-white/12 text-white" },
    { value: "campagne", label: "Campagne", color: "bg-shariti-orange/20 text-shariti-orange-light" },
    { value: "urgence", label: "Urgence", color: "bg-red-500/20 text-red-300" },
    { value: "alerte", label: "Alerte", color: "bg-yellow-500/20 text-yellow-300" },
    { value: "temoignage", label: "Témoignage", color: "bg-purple-500/20 text-purple-300" },
];

const SAMPLE_EMAIL = `Bonjour,

Voici nos dernières actualités à partager avec vos ambassadeurs cette semaine.

« Mon seul "crime" ? Être médecin » : témoignage du Dr Ahmad Mhanna

Alors qu'il soignait ses patients, l'ancien directeur de l'hôpital Al Awda, dans le nord de Gaza, a été arbitrairement arrêté par l'armée israélienne. Pendant près de deux années de détention, il a été victime de violences terribles.

https://www.amnesty.fr/actualites/temoignage-docteur-ahmad-mhanna-detenu-22-mois-par-armee-israelienne/

Les lauréats de la bourse Gaëtan Mootoo 2026

Fidèle Ahoefa Adamado, Lauraine Axelle Guina et Ndeye Astou Diouf remportent la bourse Gaëtan Mootoo, qui récompense chaque année de jeunes défenseurs francophones des droits humains.

https://www.amnesty.fr/actualites/les-laureats-de-la-bourse-gaetan-mootoo/

Bonne semaine à vous tous !`;

export default function AdminNewsImport() {
    const [associations, setAssociations] = useState([]);
    const [selectedAid, setSelectedAid] = useState("");
    const [rawText, setRawText] = useState("");
    const [parsing, setParsing] = useState(false);
    const [items, setItems] = useState(null); // null = pas encore parsé
    const [publishing, setPublishing] = useState(false);
    const [globalPushAll, setGlobalPushAll] = useState(false);

    useEffect(() => {
        api.get("/admin/associations").then((r) => {
            setAssociations(r.data || []);
        });
    }, []);

    const selectedAsso = useMemo(
        () => associations.find((a) => a.association_id === selectedAid),
        [associations, selectedAid],
    );

    const canParse = selectedAid && rawText.trim().length >= 40 && !parsing;
    const canPublish = items && items.length > 0 && !publishing;

    const handleParse = async () => {
        if (!canParse) return;
        setParsing(true);
        try {
            const { data } = await api.post(
                `/admin/associations/${selectedAid}/news/import/parse`,
                { raw_text: rawText, fetch_og_images: true },
                { timeout: 90000 },
            );
            const got = data.items || [];
            if (!got.length) {
                toast.warning("Lia n'a détecté aucune actualité claire", {
                    description: "Vérifie que le texte contient bien des actualités distinctes.",
                });
            } else {
                toast.success(`Lia a structuré ${got.length} actualité${got.length > 1 ? "s" : ""}`, {
                    description: "Vérifie et édite ce qu'elle propose, puis publie en lot.",
                });
            }
            setItems(got);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur Lia. Réessaie dans quelques secondes.");
        } finally {
            setParsing(false);
        }
    };

    const handlePublish = async () => {
        if (!canPublish) return;
        // Apply globalPushAll if user set it
        const payload = {
            items: items.map((it) => ({
                title: it.title,
                body: it.body,
                category: it.category || "info",
                attachment_url: it.attachment_url || null,
                image_url: it.image_url || null,
                pinned: !!it.pinned,
                notify_push: globalPushAll ? true : !!it.notify_push,
            })),
        };
        setPublishing(true);
        try {
            const { data } = await api.post(
                `/admin/associations/${selectedAid}/news/import/publish`,
                payload,
                { timeout: 60000 },
            );
            toast.success(
                `${data.inserted_count} actualité${data.inserted_count > 1 ? "s" : ""} publiée${
                    data.inserted_count > 1 ? "s" : ""
                } sur ${selectedAsso?.name}`,
                {
                    description: data.push_count
                        ? `Push envoyé à ${data.push_count} ambassadeur${data.push_count > 1 ? "s" : ""}.`
                        : "Visibles dans l'espace partenaire dès maintenant.",
                    duration: 6000,
                },
            );
            // Reset state
            setItems(null);
            setRawText("");
            setGlobalPushAll(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur publication.");
        } finally {
            setPublishing(false);
        }
    };

    const updateItem = (idx, patch) => {
        setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };

    const removeItem = (idx) => {
        setItems((prev) => prev.filter((_, i) => i !== idx));
    };

    const togglePin = (idx) => {
        setItems((prev) =>
            prev.map((it, i) =>
                i === idx ? { ...it, pinned: !it.pinned } : { ...it, pinned: false },
            ),
        );
    };

    return (
        <div className="space-y-6" data-testid="admin-news-import">
            {/* HERO */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-3xl border border-shariti-orange/25 bg-gradient-to-br from-shariti-orange/15 via-shariti-orange/5 to-transparent p-5 sm:p-7"
            >
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-shariti-orange/15 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-8 left-1/3 w-32 h-32 rounded-full bg-shariti-yellow/10 blur-3xl pointer-events-none" />
                <div className="relative flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-shariti-orange to-shariti-yellow flex items-center justify-center orange-glow">
                        <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                            Lia · Assistante éditoriale
                        </p>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-1.5">
                            Importer un email d&apos;actualité
                        </h2>
                        <p className="text-sm text-white/65 font-light leading-relaxed max-w-2xl">
                            Colle ici le mail brut envoyé par l&apos;association — Lia détecte chaque actu, rédige
                            un titre, un résumé punchy et extrait les liens. Tu valides, tu publies.
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* STEP 1 — Sélection asso + email brut */}
            <div className="glass-card rounded-3xl p-5 sm:p-6">
                <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-7 h-7 rounded-full bg-shariti-orange/20 flex items-center justify-center text-shariti-orange-light text-xs font-extrabold">
                        1
                    </div>
                    <h3 className="text-base sm:text-lg font-extrabold text-white">
                        Choisis l&apos;asso et colle l&apos;email
                    </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-1">
                        <label className="block text-[10px] font-thin tracking-[0.22em] uppercase text-white/55 mb-2">
                            Association cible
                        </label>
                        <ThemedSelect
                            value={selectedAid}
                            onChange={setSelectedAid}
                            dataTestid="news-import-asso-select"
                            placeholder="— Sélectionner —"
                            options={[
                                { value: "", label: "— Sélectionner —" },
                                ...associations.map((a) => ({
                                    value: a.association_id,
                                    label: a.name,
                                })),
                            ]}
                        />
                        {selectedAsso && (
                            <div className="mt-3 p-3 rounded-2xl bg-white/4 border border-white/8">
                                <p className="text-[10px] font-thin tracking-[0.18em] uppercase text-white/45 mb-1">
                                    Mission contexte
                                </p>
                                <p className="text-xs text-white/70 font-light leading-relaxed line-clamp-4">
                                    {(selectedAsso.mission || "Aucune mission renseignée.").slice(0, 280)}
                                </p>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setRawText(SAMPLE_EMAIL)}
                            className="mt-3 text-[10px] font-extrabold tracking-[0.18em] uppercase text-shariti-orange-light hover:text-shariti-orange transition-colors flex items-center gap-1.5"
                            data-testid="news-import-load-sample"
                        >
                            <Wand2 className="w-3 h-3" />
                            Charger un exemple Amnesty
                        </button>
                    </div>

                    <div className="sm:col-span-2">
                        <label className="block text-[10px] font-thin tracking-[0.22em] uppercase text-white/55 mb-2">
                            Email brut · copie-colle tout le contenu
                        </label>
                        <textarea
                            value={rawText}
                            onChange={(e) => setRawText(e.target.value)}
                            placeholder="Colle ici l'email reçu par l'association (signature, liens, paragraphes — Lia trie tout)..."
                            rows={12}
                            className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white font-light placeholder-white/30 outline-none focus:border-shariti-orange/60 transition-all text-sm leading-relaxed resize-y"
                            data-testid="news-import-raw-textarea"
                        />
                        <div className="mt-2 flex items-center justify-between text-[10px] font-thin tracking-[0.15em] uppercase text-white/40">
                            <span>{rawText.length} caractères · min 40</span>
                            {rawText.length > 25000 && (
                                <span className="text-yellow-400">
                                    Approche de la limite (30 000 max)
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-5 flex justify-end">
                    <button
                        onClick={handleParse}
                        disabled={!canParse}
                        data-testid="news-import-parse-button"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-shariti-orange to-shariti-yellow text-white font-extrabold text-sm orange-glow hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-40 disabled:scale-100 disabled:cursor-not-allowed"
                    >
                        {parsing ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Lia structure ton texte...
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" />
                                Transformer avec Lia
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* STEP 2 — Résultats Lia */}
            <AnimatePresence>
                {items && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.35 }}
                        className="space-y-4"
                        data-testid="news-import-preview"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-shariti-orange/20 flex items-center justify-center text-shariti-orange-light text-xs font-extrabold">
                                    2
                                </div>
                                <h3 className="text-base sm:text-lg font-extrabold text-white">
                                    {items.length === 0
                                        ? "Aucune actualité détectée"
                                        : `${items.length} actualité${items.length > 1 ? "s" : ""} prête${items.length > 1 ? "s" : ""}`}
                                </h3>
                            </div>
                            {items.length > 0 && (
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-2 text-xs font-bold text-white/70 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={globalPushAll}
                                            onChange={(e) => setGlobalPushAll(e.target.checked)}
                                            className="w-4 h-4 rounded accent-shariti-orange"
                                            data-testid="news-import-push-all"
                                        />
                                        <Bell className="w-3.5 h-3.5" />
                                        Push à tous les ambassadeurs
                                    </label>
                                    <button
                                        onClick={() => setItems(null)}
                                        className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-white/55 hover:text-white transition-colors flex items-center gap-1.5"
                                        data-testid="news-import-reset"
                                    >
                                        <RefreshCw className="w-3 h-3" />
                                        Recommencer
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {items.map((it, idx) => (
                                <DraftCard
                                    key={idx}
                                    item={it}
                                    index={idx}
                                    onChange={(patch) => updateItem(idx, patch)}
                                    onRemove={() => removeItem(idx)}
                                    onTogglePin={() => togglePin(idx)}
                                />
                            ))}
                        </div>

                        {items.length > 0 && (
                            <div className="sticky bottom-3 z-30 flex justify-end">
                                <button
                                    onClick={handlePublish}
                                    disabled={!canPublish}
                                    data-testid="news-import-publish-button"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-shariti-orange to-shariti-yellow text-white font-extrabold text-sm orange-glow shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-40 disabled:scale-100"
                                >
                                    {publishing ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Publication...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Publier les {items.length} actualité{items.length > 1 ? "s" : ""}
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function DraftCard({ item, index, onChange, onRemove, onTogglePin }) {
    const [editing, setEditing] = useState(false);
    const cat = CATEGORIES.find((c) => c.value === item.category) || CATEGORIES[0];

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className={`relative overflow-hidden rounded-3xl border bg-shariti-deep-black/60 backdrop-blur-sm transition-all ${
                item.pinned
                    ? "border-shariti-yellow/40 shadow-[0_0_24px_-8px_rgba(252,219,83,0.4)]"
                    : "border-white/8 hover:border-white/15"
            }`}
            data-testid={`news-import-draft-${index}`}
        >
            {item.pinned && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-shariti-yellow via-shariti-orange to-shariti-yellow" />
            )}

            {/* COVER IMAGE OG */}
            {item.image_url ? (
                <div className="relative h-40 sm:h-48 overflow-hidden bg-black/40">
                    <img
                        src={item.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />
                    <div className="absolute top-2 right-2 flex gap-1.5">
                        <button
                            type="button"
                            onClick={() => onChange({ image_url: null })}
                            className="px-2 py-1 rounded-full bg-black/60 backdrop-blur text-[9px] font-extrabold tracking-wider uppercase text-white/80 hover:text-white border border-white/15"
                            title="Retirer l'image"
                            data-testid={`news-import-clear-image-${index}`}
                        >
                            <Trash2 className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-shariti-deep-black to-transparent" />
                </div>
            ) : (
                <div className="h-24 sm:h-28 bg-gradient-to-br from-shariti-orange/10 via-white/3 to-shariti-yellow/8 flex items-center justify-center border-b border-white/5">
                    <div className="flex items-center gap-2 text-white/40 text-[10px] font-thin tracking-[0.22em] uppercase">
                        <ImageIcon className="w-3.5 h-3.5" />
                        Aucune image OG
                    </div>
                </div>
            )}

            <div className="p-4 sm:p-5">
                {/* Header : category + pin + delete */}
                <div className="flex items-center justify-between gap-2 mb-3">
                    <span
                        className={`px-2 py-1 rounded-full text-[9px] font-extrabold uppercase tracking-[0.18em] ${cat.color}`}
                    >
                        {cat.label}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={onTogglePin}
                            className={`p-1.5 rounded-full transition-colors ${
                                item.pinned
                                    ? "bg-shariti-yellow/20 text-shariti-yellow"
                                    : "text-white/45 hover:text-white hover:bg-white/8"
                            }`}
                            title={item.pinned ? "Désépingler" : "Épingler en tête"}
                            data-testid={`news-import-pin-${index}`}
                        >
                            {item.pinned ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
                        </button>
                        <button
                            onClick={() => setEditing((v) => !v)}
                            className={`p-1.5 rounded-full transition-colors ${
                                editing
                                    ? "bg-shariti-orange/20 text-shariti-orange-light"
                                    : "text-white/45 hover:text-white hover:bg-white/8"
                            }`}
                            title={editing ? "Fermer l'édition" : "Éditer"}
                            data-testid={`news-import-edit-${index}`}
                        >
                            <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={onRemove}
                            className="p-1.5 rounded-full text-white/45 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                            title="Retirer cette actu"
                            data-testid={`news-import-remove-${index}`}
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>

                {/* Title + body */}
                {editing ? (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-[9px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1">
                                Titre
                            </label>
                            <input
                                value={item.title}
                                onChange={(e) => onChange({ title: e.target.value })}
                                maxLength={240}
                                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-bold focus:outline-none focus:border-shariti-orange/50"
                                data-testid={`news-import-title-input-${index}`}
                            />
                        </div>
                        <div>
                            <label className="block text-[9px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1">
                                Résumé (200-450 car.)
                            </label>
                            <textarea
                                value={item.body}
                                onChange={(e) => onChange({ body: e.target.value })}
                                rows={4}
                                maxLength={5000}
                                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-light leading-relaxed focus:outline-none focus:border-shariti-orange/50 resize-y"
                                data-testid={`news-import-body-input-${index}`}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[9px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1">
                                    Catégorie
                                </label>
                                <ThemedSelect
                                    value={item.category}
                                    onChange={(v) => onChange({ category: v })}
                                    dataTestid={`news-import-category-${index}`}
                                    options={CATEGORIES.map((c) => ({
                                        value: c.value,
                                        label: c.label,
                                    }))}
                                />
                            </div>
                            <div>
                                <label className="block text-[9px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1">
                                    Lien article complet
                                </label>
                                <input
                                    value={item.attachment_url || ""}
                                    onChange={(e) => onChange({ attachment_url: e.target.value })}
                                    placeholder="https://..."
                                    className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-light focus:outline-none focus:border-shariti-orange/50"
                                    data-testid={`news-import-attachment-${index}`}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[9px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1">
                                Image de couverture
                            </label>
                            <FileUpload
                                kind="image"
                                currentUrl={item.image_url}
                                onUploaded={(url) => onChange({ image_url: url })}
                                testId={`news-import-image-upload-${index}`}
                            />
                        </div>
                        <label className="flex items-center gap-2 text-xs font-bold text-white/70 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!item.notify_push}
                                onChange={(e) => onChange({ notify_push: e.target.checked })}
                                className="w-4 h-4 rounded accent-shariti-orange"
                                data-testid={`news-import-push-${index}`}
                            />
                            <Bell className="w-3.5 h-3.5" />
                            Notifier par push à la publication
                        </label>
                    </div>
                ) : (
                    <>
                        <h4 className="text-base sm:text-lg font-extrabold text-white leading-tight mb-2 line-clamp-3">
                            {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-white/65 font-light leading-relaxed mb-3 line-clamp-5">
                            {item.body}
                        </p>
                        {item.attachment_url && (
                            <a
                                href={item.attachment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[10px] font-extrabold tracking-[0.18em] uppercase text-shariti-orange-light hover:text-shariti-orange transition-colors"
                            >
                                <Link2 className="w-3 h-3" />
                                Lien source
                            </a>
                        )}
                    </>
                )}
            </div>
        </motion.div>
    );
}
